import { z, ZodType } from 'zod';
import { GENDER, TUserDetails } from '../../apis/users/users.interfaces';
import {
  zDateStringFromInput,
  zOptionalDateStringFromInput,
} from '../zodFormHelpers';
import {
  TDocumentType,
  TDocumentStatus,
} from '../../apis/documents/documents.interfaces';

export const userValidationSchema = z.object({
  id: z.string().min(1, 'Student ID is required'),
  basicDetails: z.object({
    firstName: z
      .string()
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name must not exceed 50 characters')
      .regex(
        /^[A-Za-z\s]+$/,
        'First name should only contain letters and spaces'
      ),
    middleName: z
      .string()
      .regex(
        /^[A-Za-z\s]*$/,
        'Middle name should only contain letters and spaces'
      )
      .max(50, 'Middle name must not exceed 50 characters')
      .optional(),
    lastName: z
      .string()
      .min(2, 'Last name must be at least 2 characters')
      .max(50, 'Last name must not exceed 50 characters')
      .regex(
        /^[A-Za-z\s]+$/,
        'Last name should only contain letters and spaces'
      ),
    gender: z.nativeEnum(GENDER, {
      errorMap: () => ({ message: 'Please select a valid gender' }),
    }),
  }),
  actions: z.object({
    created: z.object({
      userId: z.string().min(1, 'User ID is required'),
      timestamp: z.number().int().positive(),
    }),
  }),
  contactDetails: z.object({
    email: z.object({
      email: z
        .string()
        .email('Please enter a valid email address')
        .min(5, 'Email is required')
        .max(255, 'Email is too long'),
      isVerified: z.boolean(),
    }),
    phone: z.object({
      countryCode: z
        .string()
        .min(1, 'Country code is required')
        .max(16, 'Invalid country code')
        .regex(/^\+[1-9]\d{0,14}$/, 'Invalid country code format'),
      phoneNumber: z
        .string()
        .min(8, 'Phone number must be at least 8 digits')
        .max(15, 'Phone number must not exceed 15 digits')
        .regex(/^\d+$/, 'Phone number should only contain digits'),
      isVerified: z.boolean(),
    }),
    address: z.object({
      address1: z
        .string()
        .min(5, 'Address must be at least 5 characters')
        .max(200, 'Address must not exceed 200 characters'),
      address2: z
        .string()
        .max(200, 'Address must not exceed 200 characters')
        .refine(
          (val) => !val || val.length >= 5,
          'Address must be at least 5 characters'
        )
        .optional(),
      city: z
        .string()
        .min(2, 'City must be at least 2 characters')
        .max(100, 'City must not exceed 100 characters')
        .regex(/^[A-Za-z\s]+$/, 'City should only contain letters and spaces'),
      state: z
        .string()
        .min(2, 'State must be at least 2 characters')
        .max(100, 'State must not exceed 100 characters')
        .regex(/^[A-Za-z\s]+$/, 'State should only contain letters and spaces'),
      country: z
        .string()
        .min(2, 'Country must be at least 2 characters')
        .max(100, 'Country must not exceed 100 characters')
        .regex(
          /^[A-Za-z\s]+$/,
          'Country should only contain letters and spaces'
        )
        .optional(),
      postalCode: z
        .string()
        .min(4, 'Postal code must be at least 4 characters')
        .max(10, 'Postal code must not exceed 10 characters')
        .regex(/^[A-Za-z0-9\s-]+$/, 'Invalid postal code format'),
    }),
  }),
  documents: z.array(
    z.object({
      documentId: z.string(),
      userId: z.string(),
      documentType: z.nativeEnum(TDocumentType),
      documentName: z.string(),
      submissionDate: zDateStringFromInput(),
      verificationDate: zOptionalDateStringFromInput(),
      status: z.nativeEnum(TDocumentStatus),
      remarks: z.string().optional(),
      fileUrl: z.string().optional(),
      description: z.string().optional(),
      uploadedAt: z.object({
        userId: z.string(),
        timestamp: z.number(),
      }),
    })
  ),
}) as ZodType<TUserDetails>;

export const userDefaultValue = {
  id: '',
  basicDetails: {
    firstName: '',
    middleName: '',
    lastName: '',
    gender: 'select',
  },
  actions: {
    created: {
      userId: '', // You might want to set this from your auth context
      timestamp: Date.now(),
    },
  },
  contactDetails: {
    email: {
      email: '',
      isVerified: false,
    },
    phone: {
      countryCode: '+91', // Default country code
      phoneNumber: '',
      isVerified: false,
    },
    address: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
    },
  },
  documents: [],
};
